import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDynamicPageMeta } from "../../hooks/useDynamicPageMeta";
import { useAuth } from "../../store/AuthContext";
import { deleteReferral, IReferral, listReferrals } from "../../Services/Referral";
import { exportStyledExcel } from "../../utils/excel/exportStyledExcel";
import StaffReferralPager from "./components/StaffReferralPager";
import StaffReferralTable from "./components/StaffReferralTable";
import { apiErrorMessage, formatReferralWhen, formatUsDate, pageWindow, partyCell, relationshipLabel, shortFirmName, toEpochMs } from "./referralFormat";
import {
  referralFieldClass,
  referralMemberActionClass,
  referralMemberBackClass,
  referralMemberCellClass,
  referralMemberCopyClass,
  referralMemberCountClass,
  referralMemberExportClass,
  referralMemberHeadClass,
  referralMemberHeadEdgeClass,
  referralMemberHeadMiddleClass,
  referralMemberPageClass,
  referralMemberPartyFirmClass,
  referralMemberPartyNameClass,
  referralMemberPagerActiveClass,
  referralMemberPagerGapClass,
  referralMemberPagerIdleClass,
  referralMemberSectionClass,
  referralMemberSortClass,
  referralMemberSubmitClass,
  referralMemberSubmitLabelClass,
  referralMemberTableClass,
  referralMemberTitleClass,
  referralMemberToolbarClass,
  referralRowClass,
  referralPageClass,
  referralStaffAddClass,
  referralStaffExportClass,
  referralStaffHeaderClass,
  referralStaffTitleClass,
  referralStaffToolbarClass,
  ReferralLoading,
  ReferralPanel,
  ReferralTable,
} from "./referralSurface";

type Variant = "member" | "staff";

type Props = {
  variant: Variant;
  firmLegacyId?: number | null;
  firmCosmosId?: string | null;
  basePath: string;
};

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 400;
const EXPORT_PAGE_SIZE = 200;
const MEMBER_PAGE_SIZE = 10;

type MemberSortKey = "date" | "by" | "to" | "type" | "fees";

type MemberSort = { key: MemberSortKey; direction: 1 | -1 } | null;

const memberColumns: { label: string; width: string; edge: boolean; sortKey?: MemberSortKey }[] = [
  { label: "#", width: "w-[4.86%]", edge: true },
  { label: "Referral Date", width: "w-[14.4%]", edge: false, sortKey: "date" },
  { label: "Referred By", width: "w-[14.06%]", edge: false, sortKey: "by" },
  { label: "Referred To", width: "w-[14.4%]", edge: false, sortKey: "to" },
  { label: "Type", width: "w-[14.44%]", edge: false, sortKey: "type" },
  { label: "Estimated Fees", width: "w-[14.15%]", edge: false, sortKey: "fees" },
  { label: "Action", width: "w-[23.7%]", edge: true },
];

export default function ReferralsList({ variant, firmLegacyId, firmCosmosId, basePath }: Props) {
  const { loggedPerson } = useAuth();
  const legacyId = firmLegacyId ?? (loggedPerson?.firmLegacyId ? Number(loggedPerson.firmLegacyId) : null);
  const cosmosId = firmCosmosId ?? loggedPerson?.firm ?? null;

  if (variant === "member") {
    return (
      <MemberList
        legacyId={legacyId}
        cosmosId={cosmosId}
        basePath={basePath}
      />
    );
  }
  return (
    <StaffList
      legacyId={legacyId ?? null}
      cosmosId={cosmosId}
      basePath={basePath}
      scoped={Boolean(firmCosmosId || firmLegacyId)}
    />
  );
}

function MemberList({ legacyId, cosmosId, basePath }: { legacyId: number | null; cosmosId: string | null; basePath: string }) {
  const { setPageMeta, clearPageMeta } = useDynamicPageMeta();
  const [tab, setTab] = useState<"referrals" | "collaborations">("referrals");
  const [inbound, setInbound] = useState<IReferral[]>([]);
  const [outbound, setOutbound] = useState<IReferral[]>([]);
  const [collaborations, setCollaborations] = useState<IReferral[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setPageMeta({
      title: "WSG Referrals & Collaborations",
      breadcrumbLabels: { "/referrals": "Firm Referrals" },
    });
    return () => {
      clearPageMeta();
    };
  }, [clearPageMeta, setPageMeta]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const scope = { firmId: legacyId || undefined, firmCosmosId: cosmosId || undefined, page: 1, pageSize: 500 };
        const [inRows, outRows, collabRows] = await Promise.all([
          listReferrals({ ...scope, tab: "inbound" }),
          listReferrals({ ...scope, tab: "outbound" }),
          listReferrals({ ...scope, tab: "collaborations" }),
        ]);
        if (cancelled) {
          return;
        }
        setInbound(inRows.items);
        setOutbound(outRows.items);
        setCollaborations(collabRows.items);
        setError("");
      } catch (loadError) {
        if (!cancelled) {
          setError(apiErrorMessage(loadError, "Referrals could not be loaded."));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [legacyId, cosmosId]);

  return (
    <div className={referralMemberPageClass}>
      <Link className={referralMemberBackClass} to="/mywsg">
        <svg viewBox="0 0 16 16" className="h-3.5 w-3.5 shrink-0" aria-hidden="true">
          <circle cx="8" cy="8" r="8" className="fill-current" />
          <path d="M9.4 4.4 5.8 8l3.6 3.6" fill="none" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        MY WSG
      </Link>
      <h2 className={referralMemberTitleClass}>Manage WSG Referrals & Collaborations</h2>
      <p className={referralMemberCopyClass}>WSG professionals can use this tool to submit referrals and collaborations among member firms. All submissions both inbound and outbound will be listed in order to keep track of collaborations over time. Referrals and collaborations submitted by your firm can be updated, while referrals and collaborations to your firm submitted by other members will be read only files.</p>
      <p className={referralMemberCopyClass}>Please note many referrals and collaborations made among WSG members go unreported therefore this list may not be an accurate reflection of the true activity. WSG will continue to make every effort to assist in the reporting efforts of activity among the members to assist in the accuracy of this information.</p>
      {error && <p className="mb-5 text-feedback-error-default">{error}</p>}
      <div className="flex w-full flex-col">
        <Link className={referralMemberSubmitClass} to={`${basePath}/new`}>
          <span className={referralMemberSubmitLabelClass}>Submit a Referral or Collaboration</span>
          <MemberCaret direction="right" />
        </Link>
        <div className="mt-8 border-t border-gray-300" />
        <div className="mt-8 flex items-stretch gap-1.5 border-b border-gray-400">
          <button type="button" className={memberTabClass(tab === "referrals")} onClick={() => setTab("referrals")}>
            <span>Referrals</span>
            <MemberCaret direction="down" />
          </button>
          <button type="button" className={memberTabClass(tab === "collaborations")} onClick={() => setTab("collaborations")}>
            <span>Collaborations</span>
            <MemberCaret direction="right" />
          </button>
        </div>
        <div className="mt-5 flex flex-col gap-5">
          {loading ? <ReferralLoading /> : tab === "referrals" ? (
            <>
              <MemberTable title="In-BOUND REFERRALS" rows={inbound} basePath={basePath} />
              <MemberTable title="OUT-BOUND Referrals" rows={outbound} basePath={basePath} />
            </>
          ) : (
            <MemberTable title="Collaborations" rows={collaborations} basePath={basePath} />
          )}
        </div>
      </div>
    </div>
  );
}

function memberTabClass(active: boolean): string {
  const tone = active ? "bg-gray-font text-white" : "bg-[#a2a2a2] text-white hover:bg-gray-500";
  return `inline-flex w-1/4 items-center justify-between pt-3 pr-10 pb-[10px] pl-[15px] text-start font-decimal text-sm font-semibold uppercase leading-[21px] ${tone}`;
}

function MemberCaret({ direction }: { direction: "right" | "down" }) {
  return (
    <svg viewBox="0 0 16 16" className={`h-4 w-4 shrink-0 ${direction === "down" ? "rotate-90" : ""}`} fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <path d="M6 3.5 10.5 8 6 12.5" />
    </svg>
  );
}

function PagerChevron({ direction, double = false }: { direction: "left" | "right"; double?: boolean }) {
  return (
    <svg viewBox="0 0 16 16" className={`h-4 w-4 shrink-0 ${direction === "left" ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      {double ? <path d="M3.5 3.5 8 8l-4.5 4.5M8.5 3.5 13 8l-4.5 4.5" /> : <path d="M6 3.5 10.5 8 6 12.5" />}
    </svg>
  );
}

function SortIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5 shrink-0 text-gray-400" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <path d="M4.5 6 8 2.5 11.5 6M4.5 10 8 13.5 11.5 10" />
    </svg>
  );
}

function PartyCell({ name, firm }: { name?: string; firm?: string }) {
  const person = (name ?? "").trim();
  return (
    <span title={firm}>
      {person && <span className={referralMemberPartyNameClass}>{person} /</span>}
      <em className={referralMemberPartyFirmClass}>{shortFirmName(firm)}</em>
    </span>
  );
}

function sortState(sort: MemberSort, key: MemberSortKey | undefined): "ascending" | "descending" | undefined {
  if (!key || sort?.key !== key) {
    return undefined;
  }
  return sort.direction === 1 ? "ascending" : "descending";
}

function memberSortValue(row: IReferral, key: MemberSortKey): string | number {
  if (key === "date") {
    return toEpochMs(row.isMultiYear ? row.fromDate : row.referralDate) ?? 0;
  }
  if (key === "by") {
    return partyCell(row.sendByName, row.firmByName).toLowerCase();
  }
  if (key === "to") {
    return partyCell(row.sendToName, row.firmToName).toLowerCase();
  }
  if (key === "type") {
    return (row.referralType ?? "").toLowerCase();
  }
  return (row.estimatedFeesDetail ?? "").toLowerCase();
}

function sortMemberRows(rows: IReferral[], sort: MemberSort): IReferral[] {
  if (!sort) {
    return rows;
  }
  return [...rows].sort((left, right) => {
    const a = memberSortValue(left, sort.key);
    const b = memberSortValue(right, sort.key);
    if (a === b) {
      return 0;
    }
    return (a > b ? 1 : -1) * sort.direction;
  });
}

function MemberTable({ title, rows, basePath }: { title: string; rows: IReferral[]; basePath: string }) {
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<MemberSort>(null);
  const pageCount = Math.max(1, Math.ceil(rows.length / MEMBER_PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const from = rows.length === 0 ? 0 : (safePage - 1) * MEMBER_PAGE_SIZE + 1;
  const to = Math.min(safePage * MEMBER_PAGE_SIZE, rows.length);
  const sorted = sortMemberRows(rows, sort);
  const visible = sorted.slice((safePage - 1) * MEMBER_PAGE_SIZE, safePage * MEMBER_PAGE_SIZE);

  function onSort(key: MemberSortKey) {
    setSort((current) => current?.key === key ? { key, direction: current.direction === 1 ? -1 : 1 } : { key, direction: 1 });
    setPage(1);
  }

  async function onExport() {
    try {
      await exportReferralRows(title, rows);
    } catch {
      window.alert("The referrals could not be exported.");
    }
  }

  return (
    <section>
      <h3 className={referralMemberSectionClass}>{title}</h3>
      <div className={referralMemberToolbarClass}>
        <div className="flex flex-wrap items-center gap-[30px]">
          <button type="button" className={referralMemberExportClass} onClick={() => { void onExport(); }}>Export to Excel</button>
          <span className={referralMemberCountClass}>Showing {from} to {to} of {rows.length} entries</span>
        </div>
        <MemberPager page={safePage} pageCount={pageCount} onPage={setPage} />
      </div>
      <div className="border border-gray-200">
        <ReferralTable measureKey={`${title}-${visible.length}`} className={referralMemberTableClass}>
          <colgroup>
            {memberColumns.map((column) => (
              <col key={column.label} className={column.width} />
            ))}
          </colgroup>
          <thead className={referralMemberHeadClass}>
            <tr>
              {memberColumns.map((column) => (
                <th key={column.label} className={column.edge ? referralMemberHeadEdgeClass : referralMemberHeadMiddleClass} aria-sort={sortState(sort, column.sortKey)}>
                  {column.sortKey ? (
                    <button type="button" className={referralMemberSortClass} onClick={() => onSort(column.sortKey as MemberSortKey)}>
                      {column.label}
                      <SortIcon />
                    </button>
                  ) : column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr className="bg-white">
                <td className={referralMemberCellClass} colSpan={7}>No referrals found.</td>
              </tr>
            ) : visible.map((row, index) => (
              <tr key={row.id} className={referralRowClass(index)}>
                <td className={referralMemberCellClass}>{(safePage - 1) * MEMBER_PAGE_SIZE + index + 1}</td>
                <td className={referralMemberCellClass}>{formatReferralWhen(row)}</td>
                <td className={referralMemberCellClass}><PartyCell name={row.sendByName} firm={row.firmByName} /></td>
                <td className={referralMemberCellClass}><PartyCell name={row.sendToName} firm={row.firmToName} /></td>
                <td className={referralMemberCellClass}>{row.referralType}</td>
                <td className={referralMemberCellClass}>{row.showFees === false ? "—" : row.estimatedFeesDetail}</td>
                <td className={referralMemberCellClass}>
                  {row.canViewDetail !== false && (
                    <Link className={referralMemberActionClass} to={`${basePath}/${row.id}`}>Details<MemberCaret direction="right" /></Link>
                  )}
                  {row.canEdit && (
                    <Link className={referralMemberActionClass} to={`${basePath}/${row.id}/edit`}>UPDATE<MemberCaret direction="right" /></Link>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </ReferralTable>
      </div>
    </section>
  );
}

function MemberPager({ page, pageCount, onPage }: { page: number; pageCount: number; onPage: (page: number) => void }) {
  if (pageCount <= 1) {
    return null;
  }
  return (
    <div className="flex items-center">
      <button type="button" className={`${referralMemberPagerIdleClass} disabled:opacity-40`} disabled={page <= 1} onClick={() => onPage(1)} aria-label="First page"><PagerChevron direction="left" double /></button>
      <button type="button" className={`${referralMemberPagerIdleClass} disabled:opacity-40`} disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="Previous page"><PagerChevron direction="left" /></button>
      {pageWindow(page, pageCount).map((item, index) => item === "…" ? (
        <span key={`gap-${index}`} className={referralMemberPagerGapClass}>…</span>
      ) : (
        <button key={item} type="button" className={item === page ? referralMemberPagerActiveClass : referralMemberPagerIdleClass} onClick={() => onPage(item)}>{item}</button>
      ))}
      <button type="button" className={`${referralMemberPagerIdleClass} disabled:opacity-40`} disabled={page >= pageCount} onClick={() => onPage(page + 1)} aria-label="Next page"><PagerChevron direction="right" /></button>
      <button type="button" className={`${referralMemberPagerIdleClass} disabled:opacity-40`} disabled={page >= pageCount} onClick={() => onPage(pageCount)} aria-label="Last page"><PagerChevron direction="right" double /></button>
    </div>
  );
}

async function exportReferralRows(title: string, rows: IReferral[]): Promise<void> {
  await exportStyledExcel({
    filename: "referrals.xlsx",
    title,
    sheetName: title,
    columns: [
      { key: "number", header: "#" },
      { key: "date", header: "Referral Date" },
      { key: "by", header: "Referred By" },
      { key: "to", header: "Referred To" },
      { key: "type", header: "Type" },
      { key: "fees", header: "Estimated Fees" },
    ],
    rows: rows.map((row, index) => ({
      number: index + 1,
      date: formatReferralWhen(row),
      by: partyCell(row.sendByName, row.firmByName),
      to: partyCell(row.sendToName, row.firmToName),
      type: row.referralType,
      fees: row.showFees === false ? "" : row.estimatedFeesDetail ?? "",
    })),
  });
}

function StaffList({ legacyId, cosmosId, basePath, scoped }: { legacyId: number | null; cosmosId: string | null; basePath: string; scoped: boolean }) {
  const [rows, setRows] = useState<IReferral[]>([]);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState("");
  const [request, setRequest] = useState({ page: 1, query: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = query.trim();
      setRequest((current) => {
        if (current.query === next) {
          return current;
        }
        return { page: 1, query: next };
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(handle);
    };
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const result = await listReferrals({
          tab: "all",
          firmId: scoped ? legacyId || undefined : undefined,
          firmCosmosId: scoped ? cosmosId || undefined : undefined,
          page: request.page,
          pageSize: PAGE_SIZE,
          q: request.query || undefined,
        });
        if (cancelled) {
          return;
        }
        setRows(result.items);
        setTotal(result.total);
        setError("");
      } catch (loadError) {
        if (!cancelled) {
          setError(apiErrorMessage(loadError, "Referrals could not be loaded."));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [legacyId, cosmosId, request, scoped]);

  async function onDelete(id: string) {
    if (!window.confirm("Are you sure you want to delete this member referral?")) {
      return;
    }
    try {
      await deleteReferral(id);
      setRows((current) => current.filter((row) => row.id !== id));
      setTotal((current) => Math.max(0, current - 1));
    } catch (deleteError) {
      setError(apiErrorMessage(deleteError, "The referral could not be deleted."));
    }
  }

  async function onExport() {
    try {
      await exportStaffReferrals({ firmId: scoped ? legacyId || undefined : undefined, firmCosmosId: scoped ? cosmosId || undefined : undefined, q: request.query || undefined });
    } catch {
      window.alert("The referrals could not be exported.");
    }
  }

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total === 0 ? 0 : (request.page - 1) * PAGE_SIZE + 1;
  const to = Math.min(request.page * PAGE_SIZE, total);
  const showing = <span>Showing {from} to {to} of {total} entries</span>;

  return (
    <div className={referralPageClass}>
      <div className={referralStaffHeaderClass}>
        <h2 className={referralStaffTitleClass}>Manage Member Referrals and Collaborations</h2>
        <Link className={referralStaffAddClass} to={`${basePath}/new`}>Add a Referral and Collaboration</Link>
      </div>
      {error && <p className="text-feedback-error-default">{error}</p>}
      <ReferralPanel>
        <div className={referralStaffToolbarClass}>
          <div className="flex flex-wrap items-center gap-4">
            {showing}
            <button type="button" className={referralStaffExportClass} onClick={() => { void onExport(); }}>Export to Excel</button>
            <label className="inline-flex items-center text-gray-500">Search: <input className={`${referralFieldClass} ml-2 !w-48`} value={query} onChange={(event) => setQuery(event.target.value)} /></label>
          </div>
          <StaffReferralPager page={request.page} pageCount={pageCount} onPage={(page) => setRequest((current) => ({ ...current, page }))} />
        </div>
        {loading ? <div className="h-32 animate-pulse rounded bg-gray-100" /> : <StaffReferralTable rows={rows} firstNumber={from} basePath={basePath} onDelete={(id) => { void onDelete(id); }} />}
        <div className={referralStaffToolbarClass}>
          {showing}
          <StaffReferralPager page={request.page} pageCount={pageCount} onPage={(page) => setRequest((current) => ({ ...current, page }))} />
        </div>
      </ReferralPanel>
    </div>
  );
}

async function exportStaffReferrals(scope: { firmId?: number; firmCosmosId?: string; q?: string }): Promise<void> {
  const rows: IReferral[] = [];
  let page = 1;
  let total = Infinity;
  while (rows.length < total) {
    const result = await listReferrals({ tab: "all", ...scope, page, pageSize: EXPORT_PAGE_SIZE });
    rows.push(...result.items);
    total = result.total;
    if (result.items.length === 0) {
      break;
    }
    page += 1;
  }
  await exportStyledExcel({
    filename: "referrals.xlsx",
    title: "Manage Member Referrals and Collaborations",
    sheetName: "Referrals",
    columns: [
      { key: "number", header: "#" },
      { key: "type", header: "Type" },
      { key: "submitted", header: "Sub. Date" },
      { key: "date", header: "Ref. Date" },
      { key: "id", header: "ID" },
      { key: "story", header: "Story ID" },
      { key: "byName", header: "By Name" },
      { key: "byFirm", header: "By Firm" },
      { key: "toName", header: "To Name" },
      { key: "toFirm", header: "To Firm" },
      { key: "submittedBy", header: "Sub. By Firm" },
      { key: "transaction", header: "Transaction" },
      { key: "fees", header: "Estimated Fees" },
      { key: "detail", header: "Detail" },
    ],
    rows: rows.map((row, index) => ({
      number: index + 1,
      type: relationshipLabel(row),
      submitted: formatUsDate(row.submissionDate),
      date: formatReferralWhen(row),
      id: row.legacyId || row.id,
      story: row.storyId ?? "",
      byName: row.sendByName ?? "",
      byFirm: row.firmByName ?? "",
      toName: row.sendToName ?? "",
      toFirm: row.firmToName ?? "",
      submittedBy: row.submittedByFirmName ?? "",
      transaction: row.referralType,
      fees: row.estimatedFeesDetail ?? "",
      detail: row.detail ?? "",
    })),
  });
}